import { Container, RenderTexture, Sprite, Texture } from 'pixi.js';
import { TILE_HALF_H, TILE_HALF_W, Z_UNIT_PX } from '@enugu/shared';
export class LightHandle {
    enabled = true;
    def;
    /** Sprite inside the lightmap (lightmap mode) or the glow sprite (tint mode). */
    sprite;
    constructor(def, sprite) {
        this.def = def;
        this.sprite = sprite;
    }
}
/** Ambient colour keyframes over a 24 h day (Enugu: ~06:30 sunrise, ~18:45 sunset, short dusk). */
const AMBIENT_KEYS = [
    [0, 0x29335e],
    [5.0, 0x323a6a],
    [6.0, 0x9c7a86],
    [6.75, 0xe8b48e],
    [8.0, 0xfff1df],
    [12.0, 0xffffff],
    [16.5, 0xfff4e4],
    [18.0, 0xf2b07c],
    [18.75, 0xb07a8c],
    [19.5, 0x4a4f84],
    [20.5, 0x2c3662],
    [24, 0x29335e],
];
function lerpColor(a, b, t) {
    const r = ((a >> 16) & 255) + ((((b >> 16) & 255) - ((a >> 16) & 255)) * t);
    const g = ((a >> 8) & 255) + ((((b >> 8) & 255) - ((a >> 8) & 255)) * t);
    const bl = (a & 255) + (((b & 255) - (a & 255)) * t);
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
}
/** Ambient light colour for an hour in [0, 24). Pure; used by Lighting and tests. */
export function ambientForHour(hour) {
    const h = ((hour % 24) + 24) % 24;
    for (let i = 1; i < AMBIENT_KEYS.length; i++) {
        const [h1, c1] = AMBIENT_KEYS[i];
        const [h0, c0] = AMBIENT_KEYS[i - 1];
        if (h <= h1)
            return lerpColor(c0, c1, (h - h0) / (h1 - h0));
    }
    return AMBIENT_KEYS[0][1];
}
/** 0 = full daylight, 1 = darkest night. */
export function darknessOf(color) {
    const r = (color >> 16) & 255;
    const g = (color >> 8) & 255;
    const b = color & 255;
    return 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
function makeGlowTexture() {
    const size = 128;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    grad.addColorStop(0.7, 'rgba(255,255,255,0.15)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    return Texture.from(c);
}
/**
 * Day/night colour grading and point lights, in two quality modes:
 *
 * - lightmap (med/high): lights are drawn additively into a low-res
 *   RenderTexture cleared to the ambient colour, which is then multiplied
 *   over the world. Lamps reveal true colours; darkness is real.
 *   Skipped entirely in daylight (ambient ~ white): zero cost by day.
 * - tint (low): the world root is tinted by the ambient colour (a GPU
 *   uniform in Pixi v8, free) and lamps get cheap additive glow sprites.
 */
export class Lighting {
    renderer;
    useLightmap;
    lightmapScale;
    worldRoot;
    glowLayer;
    lightRoot = new Container({ label: 'lightmap-root' });
    overlay;
    lightRT = null;
    glowTex;
    lights = [];
    ambient = 0xffffff;
    darkness = 0;
    /** Lightmap passes rendered in the last frame (stat). */
    passes = 0;
    constructor(renderer, opts) {
        this.renderer = renderer;
        this.useLightmap = opts.lightmap;
        this.lightmapScale = opts.lightmapScale;
        this.worldRoot = opts.worldRoot;
        this.glowLayer = opts.glowLayer;
        this.glowTex = makeGlowTexture();
        this.overlay = new Sprite({ label: 'lightmap', blendMode: 'multiply', visible: false });
    }
    get mode() {
        return this.useLightmap ? 'lightmap' : 'tint';
    }
    addLight(def) {
        const s = new Sprite({ texture: this.glowTex, anchor: 0.5, blendMode: 'add', tint: def.color });
        const h = new LightHandle(def, s);
        if (this.useLightmap)
            this.lightRoot.addChild(s);
        else
            this.glowLayer.addChild(s);
        this.lights.push(h);
        return h;
    }
    setAmbient(color) {
        this.ambient = color;
        this.darkness = darknessOf(color);
    }
    /** Viewport changed (CSS px). */
    resize(w, h) {
        if (!this.useLightmap)
            return;
        const lw = Math.max(1, Math.ceil(w * this.lightmapScale));
        const lh = Math.max(1, Math.ceil(h * this.lightmapScale));
        if (!this.lightRT) {
            this.lightRT = RenderTexture.create({ width: lw, height: lh, resolution: 1, scaleMode: 'linear', label: 'lightmap', dynamic: true });
            this.overlay.texture = this.lightRT;
        }
        else if (this.lightRT.width !== lw || this.lightRT.height !== lh) {
            this.lightRT.resize(lw, lh);
        }
        this.overlay.setSize(w, h);
    }
    /** Per render frame, after the camera has been applied. */
    update(camera, timeSec) {
        this.passes = 0;
        const night = this.darkness > 0.06;
        for (const l of this.lights) {
            const d = l.def;
            const flick = d.flicker ? 1 - d.flicker * (0.5 + 0.5 * Math.sin(timeSec * 23 + d.x * 7.1) * Math.sin(timeSec * 9.7 + d.y * 3.3)) : 1;
            const on = l.enabled && night;
            if (l.sprite.visible !== on)
                l.sprite.visible = on;
            if (!on)
                continue;
            const sx = (d.x - d.y) * TILE_HALF_W;
            const sy = (d.x + d.y) * TILE_HALF_H - d.z * Z_UNIT_PX;
            // Ground-plane ellipse (2:1) so light pools match the iso floor.
            const diameterPx = d.radius * 2 * TILE_HALF_W * 2;
            l.sprite.position.set(sx, sy);
            l.sprite.width = diameterPx;
            l.sprite.height = diameterPx * 0.6;
            l.sprite.alpha = this.useLightmap ? d.intensity * flick : d.intensity * flick * 0.45 * Math.min(1, this.darkness * 1.6);
        }
        if (!this.useLightmap) {
            this.worldRoot.tint = this.ambient;
            return;
        }
        this.worldRoot.tint = 0xffffff;
        if (!night || !this.lightRT) {
            this.overlay.visible = false;
            return;
        }
        // Light root mirrors the camera transform, scaled down to the lightmap resolution.
        const s = this.lightmapScale;
        this.lightRoot.position.set((camera.viewW / 2 - camera.x * camera.zoom) * s, (camera.viewH / 2 - camera.y * camera.zoom) * s);
        this.lightRoot.scale.set(camera.zoom * s);
        this.renderer.render({ container: this.lightRoot, target: this.lightRT, clear: true, clearColor: this.ambient });
        this.overlay.visible = true;
        this.passes = 1;
    }
    destroy() {
        this.lightRT?.destroy(true);
        this.glowTex.destroy(true);
        this.lightRoot.destroy({ children: true });
    }
}
